import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { startImpersonation } from "@/features/admin/actions/hijack-actions"
import { Building, Calendar, LogIn, Mail, Shield, User } from "lucide-react"
import { toast } from "sonner"

import type { AuthOrganization, AuthUser } from "../types"

interface OrganizationContextHeaderProps {
  user: AuthUser
  organization?: AuthOrganization | null
}

/**
 * Header com contexto da organização e informações do usuário
 */
export function OrganizationContextHeader({ user, organization }: OrganizationContextHeaderProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <User className="text-muted-foreground h-5 w-5" />
                <h2 className="text-xl font-semibold">{user.name}</h2>
              </div>
              {user.roles && typeof user.roles === "string" && (
                <div className="flex gap-1">
                  {user.roles.split(",").map((role, index) => (
                    <Badge
                      key={index}
                      variant={role.trim() === "admin" ? "default" : "secondary"}
                      className="gap-1"
                    >
                      <Shield className="h-3 w-3" />
                      {role.trim()}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="text-muted-foreground flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>{user.email}</span>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="space-y-1 text-right">
              <Badge variant={user.onboarding_completed ? "default" : "outline"}>
                Onboarding {user.onboarding_completed ? "Concluído" : "Pendente"}
              </Badge>
              <div className="text-muted-foreground text-sm">
                ID: <span className="font-mono">{user.id.slice(0, 8)}...</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={async () => {
                try {
                  await startImpersonation(user.id)
                  window.location.href = "/dashboard"
                } catch {
                  toast.error("Erro ao acessar como usuário")
                }
              }}
            >
              <LogIn className="h-4 w-4" />
              Entrar como usuário
            </Button>
          </div>
        </div>
      </CardHeader>

      {organization && (
        <>
          <Separator />
          <CardContent className="pt-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Building className="text-muted-foreground h-4 w-4" />
                  <h3 className="font-medium">{organization.name}</h3>
                  <Badge variant="outline">{organization.type}</Badge>
                </div>

                <div className="text-muted-foreground flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>
                      Criada em {new Date(organization.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  </div>

                  <Badge variant={organization.is_active ? "default" : "secondary"}>
                    {organization.is_active ? "Ativa" : "Inativa"}
                  </Badge>

                  <Badge variant="secondary">{organization.size}</Badge>
                </div>
              </div>

              <div className="text-muted-foreground text-right text-sm">
                <div>
                  Org ID: <span className="font-mono">{organization.id.slice(0, 8)}...</span>
                </div>
              </div>
            </div>
          </CardContent>
        </>
      )}
    </Card>
  )
}
